/** Import configuration wizard.
 *
 * Lets an operator copy configuration keys from another tour operator into the
 * currently-edited target. Three-column diff grid (key, source value, target
 * value), all rows checked by default, user unchecks the rows they don't want.
 *
 * The merge is a CLIENT-SIDE operation: clicking "Merge configuration" applies
 * the selected paths to the editor's in-memory `localConfig` via `onMerge`.
 * The user then clicks the editor's own "Save changes" button to persist to
 * the backend.
 */
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SearchIcon from '@mui/icons-material/Search';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

import * as integrationsApi from '@/api/integrations';
import * as topsApi from '@/api/tourOperators';
import type { TourOperatorListItem } from '@/api/tourOperators';

import { AuthMethodsCell } from './AuthMethodsCell';
import type { AuthMethod } from './AuthMethodEditDialog';
import { useTopConfigSchema } from './useTopConfigSchema';

// Top-level keys we treat as tenant-specific even when the schema doesn't
// explicitly flag them with `x-tenant-dependent: true`. These are arrays of
// objects holding identity-provider config (Auth0 client_ids, allowed
// domains, access-group bindings) that are conceptually per-tenant — copying
// them wholesale across operators almost always breaks the target's identity
// integration. Listed here so the wizard treats them the same way it treats
// `branchio_key`, `twilio.*`, `*.webhook_auth_token`, etc.
const TENANT_SPECIFIC_TOP_KEYS = new Set(['auth_methods', 'client_auth_methods']);

interface SchemaProp {
  type?: string;
  description?: string;
  enum?: unknown[];
  'x-tenant-dependent'?: boolean;
  properties?: Record<string, SchemaProp>;
}

// ---------------------------------------------------------------------------
// Diff helpers
// ---------------------------------------------------------------------------

interface Leaf {
  path: string[];
  value: unknown;
}

/** Recursively walk a config dict, yielding one entry per leaf (non-object) value
 *  AND one entry per leaf-array (we don't drill into array indices — the array
 *  is the unit a tenant overrides). Arrays are returned as the leaf value. */
function flatten(config: Record<string, unknown>, prefix: string[] = []): Leaf[] {
  const out: Leaf[] = [];
  for (const [k, v] of Object.entries(config)) {
    if (v !== null && typeof v === 'object' && !Array.isArray(v)) {
      out.push(...flatten(v as Record<string, unknown>, [...prefix, k]));
    } else {
      out.push({ path: [...prefix, k], value: v });
    }
  }
  return out;
}

/** Look up a possibly-nested value at the given dotted path. */
function getAtPath(obj: Record<string, unknown> | null | undefined, path: string[]): unknown {
  let node: unknown = obj;
  for (const seg of path) {
    if (node === null || typeof node !== 'object' || Array.isArray(node)) return undefined;
    node = (node as Record<string, unknown>)[seg];
    if (node === undefined) return undefined;
  }
  return node;
}

/** Set a possibly-nested value at the given dotted path, returning a new object. */
function setAtPath(
  obj: Record<string, unknown>,
  path: string[],
  value: unknown,
): Record<string, unknown> {
  const [head, ...tail] = path;
  if (!head) return obj;
  if (tail.length === 0) {
    return { ...obj, [head]: value };
  }
  const child = obj[head];
  const childObj =
    child !== null && typeof child === 'object' && !Array.isArray(child)
      ? (child as Record<string, unknown>)
      : {};
  return { ...obj, [head]: setAtPath(childObj, tail, value) };
}

/** Look up the schema entry for a dotted path (returns null if unknown). */
function schemaAt(schemaProps: Record<string, SchemaProp>, path: string[]): SchemaProp | null {
  const [head, ...tail] = path;
  if (!head) return null;
  let prop = schemaProps[head] ?? null;
  for (const seg of tail) {
    if (!prop || !prop.properties) return null;
    prop = prop.properties[seg] ?? null;
  }
  return prop;
}

interface DiffRow {
  dotted: string;
  path: string[];
  sourceValue: unknown;
  targetValue: unknown;
  targetHas: boolean;
  /** True when source either omits this path entirely or the value matches the
   *  target. These rows never make it to the grid; flagged only for accounting. */
  same: boolean;
  schema: SchemaProp | null;
  tenantDependent: boolean;
}

function jsonEq(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
}

interface DiffBuildResult {
  rows: DiffRow[];
  /** Count of paths skipped because their top-level key is an integration
   *  managed via a dedicated integration screen. Surfaced in the UI so the
   *  user knows that subset isn't part of this merge by design. */
  excludedIntegrationKeys: number;
  /** Names of the top-level integration heads that contributed at least one
   *  skipped leaf — used to render a one-line "excluded: bokun, ventrata, …"
   *  caption beneath the grid. */
  excludedIntegrationHeads: string[];
}

/** Build the diff between source and target.
 *
 * Only emits rows where the source has a value AND that value differs from the
 * target (the wizard's purpose is to *copy from source* — paths absent from
 * source aren't ours to touch).
 *
 * Integration-related top-level keys (`bokun`, `fareharbor`, `ventrata`, etc.
 * — anything in `hiddenIntegrationKeys`) are excluded entirely. They are
 * managed via dedicated integration screens, not the assisted config flow.
 * `curatedplanet` is intentionally NOT in the hidden set (it's a structured
 * config group with schema-known fields), matching the Assisted editor.
 */
function buildDiff(
  source: Record<string, unknown>,
  target: Record<string, unknown>,
  hiddenIntegrationKeys: Set<string>,
  schemaProps: Record<string, SchemaProp>,
): DiffBuildResult {
  const sourceLeaves = flatten(source);
  const rows: DiffRow[] = [];
  const excludedHeads = new Set<string>();
  let excludedCount = 0;
  for (const leaf of sourceLeaves) {
    const top = leaf.path[0]!;
    if (hiddenIntegrationKeys.has(top)) {
      // Track that we dropped this leaf so we can tell the user via a caption.
      // We DON'T also check whether the value differs — even matching keys are
      // skipped, the user shouldn't be merging integration creds through the
      // assisted flow at all.
      excludedCount += 1;
      excludedHeads.add(top);
      continue;
    }
    const targetValue = getAtPath(target, leaf.path);
    const targetHas = targetValue !== undefined;
    const same = jsonEq(leaf.value, targetValue);
    if (same) continue;
    const prop = schemaAt(schemaProps, leaf.path);
    // Either the schema flagged the path tenant-specific OR the top-level key
    // is in TENANT_SPECIFIC_TOP_KEYS (auth_methods / client_auth_methods).
    const isTenantSpecificTopKey =
      leaf.path.length === 1 && TENANT_SPECIFIC_TOP_KEYS.has(leaf.path[0]!);
    rows.push({
      dotted: leaf.path.join('.'),
      path: leaf.path,
      sourceValue: leaf.value,
      targetValue,
      targetHas,
      same,
      schema: prop,
      tenantDependent: Boolean(prop?.['x-tenant-dependent']) || isTenantSpecificTopKey,
    });
  }
  return {
    rows: rows.sort((a, b) => a.dotted.localeCompare(b.dotted)),
    excludedIntegrationKeys: excludedCount,
    excludedIntegrationHeads: [...excludedHeads].sort(),
  };
}

// ---------------------------------------------------------------------------
// Value rendering
// ---------------------------------------------------------------------------

function safePreview(value: unknown, max = 80): string {
  if (value === undefined) return '—';
  if (value === null) return 'null';
  let s: string;
  try {
    s = typeof value === 'string' ? value : JSON.stringify(value);
  } catch {
    s = String(value);
  }
  return s.length > max ? `${s.slice(0, max)}…` : s;
}

// ---------------------------------------------------------------------------
// Leaf row — renders one diffed key with its source/target values
// ---------------------------------------------------------------------------

interface LeafRowProps {
  row: DiffRow;
  /** Visual indent step (matches the Assisted editor: 16 px per depth level). */
  depth: number;
  /** Show the parent breadcrumb above the leaf name. Useful for keys nested
   *  two-plus levels deep (e.g. `client_app_upgrade.ios.min_version`) where
   *  the immediate parent isn't already obvious from the surrounding group. */
  showParentBreadcrumb: boolean;
  checked: boolean;
  onToggle: () => void;
  /** The value to display in the editable middle column. Either the user's
   *  override (when set) or the raw source value. */
  effectiveValue: unknown;
  isOverridden: boolean;
  onOverride: (value: unknown) => void;
  onResetOverride: () => void;
}

function LeafRow({
  row,
  depth,
  showParentBreadcrumb,
  checked,
  onToggle,
  effectiveValue,
  isOverridden,
  onOverride,
  onResetOverride,
}: LeafRowProps) {
  const leafName = row.path[row.path.length - 1]!;
  const parentPath = row.path.slice(0, -1).join('.');
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '44px minmax(260px, 1fr) minmax(180px, 2fr) minmax(180px, 2fr)',
        gap: 1,
        alignItems: 'flex-start',
        px: 2,
        py: 0.75,
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:hover': { bgcolor: 'action.hover' },
        opacity: checked ? 1 : 0.55,
      }}
    >
      <Checkbox
        size="small"
        checked={checked}
        onChange={onToggle}
        sx={{ p: 0.25 }}
      />

      <Box sx={{ minWidth: 0, pl: `${depth * 16}px` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography
            variant="body2"
            sx={{
              fontFamily: 'monospace',
              fontSize: 12,
              fontWeight: depth === 0 ? 600 : 400,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={row.dotted}
          >
            {leafName}
          </Typography>
          {row.tenantDependent && (
            <Tooltip title="Tenant-specific key (credential/identifier). Copying is almost always wrong.">
              <WarningAmberIcon sx={{ fontSize: 14, color: 'warning.main' }} />
            </Tooltip>
          )}
          {row.schema === null && (
            <Tooltip title="Unknown key — not in the schema. Unchecked by default; verify before importing.">
              <Chip
                label="unknown"
                size="small"
                color="default"
                variant="outlined"
                sx={{ height: 16, fontSize: 10, '& .MuiChip-label': { px: 0.5 } }}
              />
            </Tooltip>
          )}
          {!row.targetHas && (
            <Chip
              label="new"
              size="small"
              color="info"
              variant="outlined"
              sx={{ height: 16, fontSize: 10, '& .MuiChip-label': { px: 0.5 } }}
            />
          )}
          {isOverridden && (
            <Chip
              label="edited"
              size="small"
              color="warning"
              variant="outlined"
              sx={{ height: 16, fontSize: 10, '& .MuiChip-label': { px: 0.5 } }}
            />
          )}
        </Box>
        {showParentBreadcrumb && parentPath && (
          <Typography
            variant="caption"
            color="text.disabled"
            sx={{ fontFamily: 'monospace', fontSize: 10 }}
          >
            {parentPath}.
          </Typography>
        )}
      </Box>

      {/* "Value to merge" column — editable for scalar/enum types. */}
      <Box sx={{ minWidth: 0 }} onClick={(e) => e.stopPropagation()}>
        <OverrideEditor
          row={row}
          value={effectiveValue}
          onChange={onOverride}
        />
        {isOverridden && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
            <Tooltip title="Reset to source value">
              <IconButton size="small" onClick={onResetOverride} sx={{ p: 0.25 }}>
                <RestartAltIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{
                fontFamily: 'monospace',
                fontSize: 10,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
              title={`Source value: ${safePreview(row.sourceValue, 1000)}`}
            >
              source: {safePreview(row.sourceValue, 40)}
            </Typography>
          </Box>
        )}
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="caption"
          sx={{
            fontFamily: 'monospace',
            fontSize: 11,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all',
            color: row.targetHas ? 'text.secondary' : 'text.disabled',
          }}
        >
          {row.targetHas ? safePreview(row.targetValue) : '—'}
        </Typography>
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Override editor — type-aware inline editor for the "Value to merge" column.
//
// Scalars and enums get a true inline editor (Switch / Select / TextField).
// Complex types (array, object) and null/undefined values fall back to a
// readonly preview — the user can choose not to import that row and edit it
// from the main editor after merging.
// ---------------------------------------------------------------------------

function OverrideEditor({
  row,
  value,
  onChange,
}: {
  row: DiffRow;
  value: unknown;
  onChange: (value: unknown) => void;
}) {
  const schemaType = row.schema?.type;
  const enumVals = row.schema?.enum;

  // Special case: auth_methods / client_auth_methods are arrays of objects
  // with a known shape and a dedicated chip+dialog editor. Render the same
  // <AuthMethodsCell> the main Assisted editor uses, so the wizard's UX for
  // these matches the surrounding editor exactly. Only match at depth 0 — a
  // nested child of some unrelated object that happens to share the name
  // shouldn't get the auth-methods treatment.
  if (
    row.path.length === 1 &&
    (row.path[0] === 'auth_methods' || row.path[0] === 'client_auth_methods')
  ) {
    const methods: AuthMethod[] = Array.isArray(value) ? (value as AuthMethod[]) : [];
    return (
      <AuthMethodsCell
        value={methods}
        onChange={(next) => onChange(next)}
      />
    );
  }

  // Boolean → Switch
  if (schemaType === 'boolean' || typeof value === 'boolean') {
    return (
      <FormControlLabel
        sx={{ m: 0 }}
        control={
          <Switch
            checked={Boolean(value)}
            onChange={(e) => onChange(e.target.checked)}
            size="small"
          />
        }
        label={
          <Typography variant="caption" color="text.secondary">
            {value ? 'true' : 'false'}
          </Typography>
        }
      />
    );
  }

  // Enum → Select (works for string and numeric enums)
  if (enumVals && enumVals.length > 0) {
    return (
      <Select
        size="small"
        value={value === undefined || value === null ? '' : (value as string | number)}
        onChange={(e) => {
          // Preserve number type when the enum is numeric.
          const raw = e.target.value;
          if (schemaType === 'integer' || schemaType === 'number') {
            onChange(typeof raw === 'number' ? raw : Number(raw));
          } else {
            onChange(raw);
          }
        }}
        sx={{ '& .MuiSelect-select': { py: 0.5, px: 1, fontSize: 12, fontFamily: 'monospace' } }}
        fullWidth
      >
        {enumVals.map((ev, i) => (
          <MenuItem key={i} value={ev as string | number} sx={{ fontSize: 12, fontFamily: 'monospace' }}>
            {String(ev)}
          </MenuItem>
        ))}
      </Select>
    );
  }

  // Integer / number → numeric TextField
  if (schemaType === 'integer' || schemaType === 'number') {
    return (
      <TextField
        size="small"
        type="number"
        value={value === undefined || value === null ? '' : (value as number)}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === '') {
            onChange(null);
            return;
          }
          const n = schemaType === 'integer' ? parseInt(raw, 10) : parseFloat(raw);
          if (!Number.isNaN(n)) onChange(n);
        }}
        InputProps={{ sx: { fontSize: 12, fontFamily: 'monospace' } }}
        fullWidth
      />
    );
  }

  // String → TextField (multiline when the source has newlines or is very long)
  if (schemaType === 'string' || typeof value === 'string') {
    const s = (value ?? '') as string;
    const isLong = typeof s === 'string' && (s.length > 80 || s.includes('\n'));
    return (
      <TextField
        size="small"
        value={typeof value === 'string' ? value : ''}
        onChange={(e) => onChange(e.target.value)}
        multiline={isLong}
        maxRows={isLong ? 6 : undefined}
        InputProps={{ sx: { fontSize: 12, fontFamily: 'monospace' } }}
        fullWidth
      />
    );
  }

  // Arrays / objects / null / unknown — readonly preview. The user can choose
  // not to import the row and tweak it from the main editor afterwards.
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Typography
        variant="caption"
        sx={{
          fontFamily: 'monospace',
          fontSize: 11,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-all',
          color: 'success.dark',
        }}
      >
        {safePreview(value)}
      </Typography>
      <Tooltip title="Complex value — uncheck this row and edit it in the main editor after the merge.">
        <Typography
          variant="caption"
          color="text.disabled"
          sx={{ fontSize: 10, fontStyle: 'italic', whiteSpace: 'nowrap' }}
        >
          (readonly)
        </Typography>
      </Tooltip>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Wizard
// ---------------------------------------------------------------------------

export interface ImportConfigWizardProps {
  open: boolean;
  /** Tour operator id of the editor's target (excluded from the source picker). */
  targetId: number;
  /** The editor's current in-memory config (may have unsaved edits). */
  targetConfig: Record<string, unknown>;
  /** Called when the user clicks "Merge configuration". Receives the new
   *  config object — the editor swaps its `localConfig` for this value. */
  onMerge: (mergedConfig: Record<string, unknown>) => void;
  onClose: () => void;
}

export function ImportConfigWizard({
  open,
  targetId,
  targetConfig,
  onMerge,
  onClose,
}: ImportConfigWizardProps) {
  const [sourceId, setSourceId] = useState<number | null>(null);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  // Top-level group keys that are currently expanded in the preview grid.
  // (Mirrors the Assisted editor's `expanded: Set<string>` convention.)
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  // Per-path value overrides — when set, the merge uses the override instead
  // of the raw source value. Reset whenever the diff set changes (source
  // operator picked, target config mutated upstream, etc.) so we never reuse
  // an override against a value it no longer makes sense for.
  const [overrides, setOverrides] = useState<Record<string, unknown>>({});

  // Reset when the dialog closes/reopens so the user starts fresh.
  useEffect(() => {
    if (!open) {
      setSourceId(null);
      setChecked(new Set());
      setSearch('');
      setExpandedGroups(new Set());
      setOverrides({});
    }
  }, [open]);

  // Source-operator picker: list of every TO except the target.
  const listQuery = useQuery({
    queryKey: ['tops', 'list-for-import'],
    queryFn: () => topsApi.list({ pageSize: 500, ordering: 'name' }),
    enabled: open,
    staleTime: 60 * 1000,
  });
  const sourceOptions: TourOperatorListItem[] = useMemo(
    () => (listQuery.data?.results ?? []).filter((t) => t.id !== targetId),
    [listQuery.data, targetId],
  );

  // Source-operator config (the per-detail endpoint returns the `config` field).
  const sourceQuery = useQuery({
    queryKey: ['top', 'detail-for-import', sourceId],
    queryFn: () => topsApi.retrieve(sourceId!),
    enabled: open && sourceId !== null,
    staleTime: 30 * 1000,
  });
  const sourceConfig = (sourceQuery.data?.config ?? null) as Record<string, unknown> | null;

  // Integration catalog — same query key as the Assisted editor so the cache is
  // shared. We drop every leaf whose top-level key is a known integration head
  // (bokun, fareharbor, ventrata, etc.); curatedplanet is the one exception
  // because it's a structured config group with schema-known fields.
  const integrationsQuery = useQuery({
    queryKey: ['integrations', 'list'],
    queryFn: () => integrationsApi.list(),
    enabled: open,
    staleTime: 60 * 60 * 1000,
  });
  const hiddenIntegrationKeys = useMemo(() => {
    const ids = (integrationsQuery.data?.results ?? []).map((i) => i.id);
    return new Set(ids.filter((id) => id !== 'curatedplanet'));
  }, [integrationsQuery.data]);

  // Served top-level config schema — shared cache with the Assisted editor
  // and the batch config page. Gates the diff: tenant-dependent flags and
  // per-key schema lookups come from it.
  const {
    schemaProps,
    isLoading: schemaLoading,
    error: schemaError,
    refetch: refetchSchema,
  } = useTopConfigSchema();

  // Diff against the (potentially dirty) target config.
  const diffResult = useMemo(
    () =>
      sourceConfig && schemaProps
        ? buildDiff(sourceConfig, targetConfig, hiddenIntegrationKeys, schemaProps)
        : { rows: [], excludedIntegrationKeys: 0, excludedIntegrationHeads: [] },
    [sourceConfig, targetConfig, hiddenIntegrationKeys, schemaProps],
  );
  const diffRows = diffResult.rows;

  // Default-check every diffed row EXCEPT:
  //  - Tenant-specific keys — schema-flagged `x-tenant-dependent: true` plus
  //    `auth_methods` / `client_auth_methods`. Copying credentials/identity
  //    configs across operators is almost always wrong.
  //  - Unknown keys (not in schema). They might be legacy / typo'd / unmanaged
  //    properties of the source operator; we don't know enough about them to
  //    safely propagate them by default. The user can opt in row by row.
  // The Alert banner still fires the moment a tenant-specific row is checked
  // so the warning isn't lost.
  // Overrides also reset whenever the diff set changes — any earlier override
  // was keyed to a value the user can no longer see, so keeping it would be
  // surprising.
  useEffect(() => {
    setOverrides({});
    if (diffRows.length === 0) {
      setChecked(new Set());
      return;
    }
    setChecked(
      new Set(
        diffRows
          .filter((r) => !r.tenantDependent && r.schema !== null)
          .map((r) => r.dotted),
      ),
    );
  }, [diffRows]);

  const filteredRows = useMemo(() => {
    if (!search) return diffRows;
    const lc = search.toLowerCase();
    return diffRows.filter((r) => r.dotted.toLowerCase().includes(lc));
  }, [diffRows, search]);

  /** Group filtered rows by their top-level key. Top-level scalar diffs
   *  (path.length === 1) get a single-leaf group rendered as a flat row;
   *  multi-segment paths get a collapsible parent row that owns the leaves. */
  interface DiffGroup {
    topKey: string;
    children: DiffRow[];   // sorted by full dotted path
    isFlat: boolean;       // true when the group has exactly one leaf at depth 0
  }
  const groups: DiffGroup[] = useMemo(() => {
    const byTop = new Map<string, DiffRow[]>();
    for (const r of filteredRows) {
      const top = r.path[0]!;
      const arr = byTop.get(top);
      if (arr) arr.push(r);
      else byTop.set(top, [r]);
    }
    return [...byTop.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([topKey, children]) => ({
        topKey,
        children: children.sort((a, b) => a.dotted.localeCompare(b.dotted)),
        isFlat: children.length === 1 && children[0]!.path.length === 1,
      }));
  }, [filteredRows]);

  /** When the user types into the search box, auto-expand any group whose
   *  children matched — mirrors the Assisted editor's "child match" behavior. */
  const effectivelyExpanded = useMemo(() => {
    if (!search) return expandedGroups;
    const s = new Set(expandedGroups);
    for (const g of groups) if (!g.isFlat) s.add(g.topKey);
    return s;
  }, [search, expandedGroups, groups]);

  function toggleGroup(topKey: string) {
    setExpandedGroups((prev) => {
      const s = new Set(prev);
      if (s.has(topKey)) s.delete(topKey);
      else s.add(topKey);
      return s;
    });
  }

  const allFilteredChecked = filteredRows.length > 0 && filteredRows.every((r) => checked.has(r.dotted));
  const someFilteredChecked = filteredRows.some((r) => checked.has(r.dotted));

  function toggleAll() {
    const next = new Set(checked);
    if (allFilteredChecked) {
      filteredRows.forEach((r) => next.delete(r.dotted));
    } else {
      filteredRows.forEach((r) => next.add(r.dotted));
    }
    setChecked(next);
  }

  function toggleRow(dotted: string) {
    const next = new Set(checked);
    if (next.has(dotted)) next.delete(dotted);
    else next.add(dotted);
    setChecked(next);
  }

  /** Check / uncheck every leaf in a group with one click. */
  function toggleGroupChecks(group: DiffGroup) {
    const next = new Set(checked);
    const allOn = group.children.every((c) => next.has(c.dotted));
    for (const c of group.children) {
      if (allOn) next.delete(c.dotted);
      else next.add(c.dotted);
    }
    setChecked(next);
  }

  function setOverride(dotted: string, value: unknown) {
    setOverrides((prev) => ({ ...prev, [dotted]: value }));
  }
  function resetOverride(dotted: string) {
    setOverrides((prev) => {
      if (!(dotted in prev)) return prev;
      const next = { ...prev };
      delete next[dotted];
      return next;
    });
  }

  function handleMerge() {
    if (!sourceConfig) return;
    const selectedRows = diffRows.filter((r) => checked.has(r.dotted));
    let next = targetConfig;
    for (const r of selectedRows) {
      // Override beats source value; fall back to the raw source value when
      // the user didn't touch the row.
      const value = r.dotted in overrides
        ? overrides[r.dotted]
        : getAtPath(sourceConfig, r.path);
      next = setAtPath(next, r.path, value);
    }
    onMerge(next);
    onClose();
  }

  const tenantDependentChecked = diffRows.filter(
    (r) => r.tenantDependent && checked.has(r.dotted),
  ).length;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Import configuration from another tour operator</DialogTitle>
      <DialogContent dividers sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
        {/* Source picker + search */}
        <Box
          sx={{
            p: 2,
            display: 'flex',
            gap: 2,
            alignItems: 'center',
            flexWrap: 'wrap',
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Autocomplete
            size="small"
            sx={{ width: 360 }}
            options={sourceOptions}
            loading={listQuery.isLoading}
            getOptionLabel={(o) => `${o.name} (${o.code})`}
            value={sourceOptions.find((o) => o.id === sourceId) ?? null}
            onChange={(_, v) => setSourceId(v?.id ?? null)}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderInput={(params) => (
              <TextField {...params} placeholder="Source tour operator…" autoFocus />
            )}
          />
          {sourceConfig && (
            <TextField
              size="small"
              placeholder="Filter keys…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ width: 240 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: search ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearch('')}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
          )}
          <Box sx={{ flexGrow: 1 }} />
          {sourceConfig && diffRows.length > 0 && (
            <Typography variant="caption" color="text.secondary">
              <strong>{checked.size}</strong> of {diffRows.length} key{diffRows.length !== 1 ? 's' : ''} selected
              {tenantDependentChecked > 0 && (
                <>
                  {' · '}
                  <Box component="span" sx={{ color: 'warning.main' }}>
                    {tenantDependentChecked} tenant-specific
                  </Box>
                </>
              )}
            </Typography>
          )}
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, minHeight: 360, maxHeight: 'calc(100vh - 320px)', overflowY: 'auto' }}>
          {/* Schema failed to load — the diff can't be classified without it */}
          {schemaError && (
            <Alert
              severity="error"
              sx={{ m: 2 }}
              action={
                <Button color="inherit" size="small" onClick={() => refetchSchema()}>
                  Retry
                </Button>
              }
            >
              Couldn’t load the configuration schema: {schemaError.message}
            </Alert>
          )}

          {/* No source picked yet */}
          {!sourceId && (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Pick a tour operator above to compare its configuration with this one.
              </Typography>
            </Box>
          )}

          {/* Loading source config (or the schema that classifies the diff) */}
          {sourceId && (sourceQuery.isLoading || schemaLoading) && (
            <Box sx={{ p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">
                Loading source configuration…
              </Typography>
            </Box>
          )}

          {/* Source loaded but no diff */}
          {sourceId && !sourceQuery.isLoading && !schemaLoading && !schemaError && sourceConfig && diffRows.length === 0 && (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No configuration differences — every key the source has is already
                set to the same value in this tour operator (or the source has no
                config keys to import).
              </Typography>
              {diffResult.excludedIntegrationKeys > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  {diffResult.excludedIntegrationKeys} integration-managed key
                  {diffResult.excludedIntegrationKeys !== 1 ? 's were' : ' was'}{' '}
                  excluded ({diffResult.excludedIntegrationHeads.join(', ')}). Manage
                  those via the dedicated integration screens.
                </Typography>
              )}
            </Box>
          )}

          {/* Source loaded — render 3-column grid */}
          {sourceConfig && diffRows.length > 0 && (
            <>
              {tenantDependentChecked > 0 && (
                <Alert severity="warning" sx={{ m: 2, mb: 1 }}>
                  {tenantDependentChecked} selected key
                  {tenantDependentChecked !== 1 ? 's are' : ' is'} tenant-specific
                  (credentials, IDs, etc.). Copying these usually breaks the target
                  tenant. Uncheck them unless you really intend to overwrite.
                </Alert>
              )}

              {diffResult.excludedIntegrationKeys > 0 && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mx: 2, mt: 1, mb: 0.5 }}
                >
                  {diffResult.excludedIntegrationKeys} integration-managed key
                  {diffResult.excludedIntegrationKeys !== 1 ? 's' : ''} excluded
                  from the merge ({diffResult.excludedIntegrationHeads.join(', ')}).
                  Manage those via the dedicated integration screens.
                </Typography>
              )}

              {/* Header row */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '44px minmax(260px, 1fr) minmax(180px, 2fr) minmax(180px, 2fr)',
                  gap: 1,
                  alignItems: 'center',
                  px: 2,
                  py: 1,
                  position: 'sticky',
                  top: 0,
                  zIndex: 1,
                  bgcolor: 'background.paper',
                  borderBottom: 1,
                  borderColor: 'divider',
                }}
              >
                <Checkbox
                  size="small"
                  checked={allFilteredChecked}
                  indeterminate={!allFilteredChecked && someFilteredChecked}
                  onChange={toggleAll}
                  sx={{ p: 0.25 }}
                />
                <Typography variant="caption" sx={{ fontWeight: 600 }}>Key</Typography>
                <Tooltip title="Defaults to the source value; edit inline to override before merging." placement="top">
                  <Typography variant="caption" sx={{ fontWeight: 600, cursor: 'help' }}>
                    Value to merge
                  </Typography>
                </Tooltip>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>Target value</Typography>
              </Box>

              {filteredRows.length === 0 && (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    No keys match your filter.
                  </Typography>
                </Box>
              )}

              {/* Grouped rendering — top-level parents collapse like in the Assisted editor */}
              {groups.map((group) => {
                const isExpanded = effectivelyExpanded.has(group.topKey);
                const checkedCount = group.children.filter((c) => checked.has(c.dotted)).length;
                const allCheckedInGroup = checkedCount === group.children.length;
                const anyCheckedInGroup = checkedCount > 0;
                const anyTenantDepInGroup = group.children.some((c) => c.tenantDependent);

                // Flat single-leaf group: render as a regular leaf row, no parent
                // wrapper or chevron — matches how the Assisted editor renders
                // top-level scalars.
                if (group.isFlat) {
                  const r = group.children[0]!;
                  const isOverridden = r.dotted in overrides;
                  const effectiveValue = isOverridden ? overrides[r.dotted] : r.sourceValue;
                  return (
                    <LeafRow
                      key={group.topKey}
                      row={r}
                      depth={0}
                      showParentBreadcrumb={false}
                      checked={checked.has(r.dotted)}
                      onToggle={() => toggleRow(r.dotted)}
                      effectiveValue={effectiveValue}
                      isOverridden={isOverridden}
                      onOverride={(v) => setOverride(r.dotted, v)}
                      onResetOverride={() => resetOverride(r.dotted)}
                    />
                  );
                }

                return (
                  <Box key={group.topKey}>
                    {/* Parent (collapsible) row */}
                    <Box
                      onClick={() => toggleGroup(group.topKey)}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns:
                          '44px minmax(260px, 1fr) minmax(180px, 2fr) minmax(180px, 2fr)',
                        gap: 1,
                        alignItems: 'center',
                        px: 2,
                        py: 0.75,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        cursor: 'pointer',
                        bgcolor: isExpanded ? 'action.hover' : 'transparent',
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                    >
                      <Checkbox
                        size="small"
                        checked={allCheckedInGroup}
                        indeterminate={!allCheckedInGroup && anyCheckedInGroup}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => toggleGroupChecks(group)}
                        sx={{ p: 0.25 }}
                      />

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); toggleGroup(group.topKey); }}
                          sx={{ p: 0.25 }}
                        >
                          {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        </IconButton>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: 'monospace',
                            fontSize: 12,
                            fontWeight: 600,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {group.topKey}
                        </Typography>
                        {anyTenantDepInGroup && (
                          <Tooltip title="This group contains tenant-specific keys (credentials/identifiers). Copying them is almost always wrong.">
                            <WarningAmberIcon sx={{ fontSize: 14, color: 'warning.main' }} />
                          </Tooltip>
                        )}
                      </Box>

                      {/* Summary in the "Source value" column: how many keys diff and how many are checked */}
                      <Typography variant="caption" color="text.disabled" sx={{ fontFamily: 'monospace', fontSize: 11 }}>
                        {`{${group.children.length} key${group.children.length !== 1 ? 's' : ''} to merge}`}
                      </Typography>

                      {/* Summary in the "Target value" column: selection state */}
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                        {checkedCount}/{group.children.length} selected
                      </Typography>
                    </Box>

                    {/* Children — only when expanded */}
                    {isExpanded &&
                      group.children.map((r) => {
                        const isOverridden = r.dotted in overrides;
                        const effectiveValue = isOverridden
                          ? overrides[r.dotted]
                          : r.sourceValue;
                        return (
                          <LeafRow
                            key={r.dotted}
                            row={r}
                            // Indent at least 1 level inside an expanded group, plus
                            // any extra depth for keys nested two-plus levels deep.
                            depth={r.path.length - 1}
                            showParentBreadcrumb={r.path.length > 2}
                            checked={checked.has(r.dotted)}
                            onToggle={() => toggleRow(r.dotted)}
                            effectiveValue={effectiveValue}
                            isOverridden={isOverridden}
                            onOverride={(v) => setOverride(r.dotted, v)}
                            onResetOverride={() => resetOverride(r.dotted)}
                          />
                        );
                      })}
                  </Box>
                );
              })}
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto', maxWidth: 420 }}>
          The merge is applied in-memory. Click <strong>Save changes</strong> in the editor afterwards to persist to the backend.
        </Typography>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!sourceConfig || checked.size === 0}
          onClick={handleMerge}
        >
          Merge configuration ({checked.size})
        </Button>
      </DialogActions>
    </Dialog>
  );
}
